import { CertifiedBaseIcon } from '@tableau/icons/CertifiedBaseIcon';
import { KeyVerticalOutlineIcon } from '@tableau/icons/KeyVerticalOutlineIcon';
import { RESERVEDDatasourceCanInfoIcon } from '@tableau/icons/RESERVEDDatasourceCanInfoIcon';
import { RESERVEDDatasourceCanWarningIcon } from '@tableau/icons/RESERVEDDatasourceCanWarningIcon';
import { divWithStyle } from '@tableau/vizportalclient-common-components';
import { ExpandoWidget, ExpandoDirection, TabStyles, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import { Formatters, Messages } from '../.././../../../../gen-src/Localize';
import { TableauUser, UserLinkDisplayComponent, UserLinkProps, UserTextDisplayComponent } from '../../UserDisplayComponent';
import { SingleLineLinkWithSubLinkIcon, SingleLineTextWithIcon, SingleLineLinkWithIcon } from '../../ViewerPaneLayoutComponents';
import { FormattedMarkupWrapperInTooltip } from '../../style/MarkupStyles';
import { linkClass } from '../../style/VizPortalStyles';
import { interpolateString } from '../../utils/StringInterpolation';
import { assertUnreachable } from '../../utils/TypescriptHelpers';
import { RenderLabelAssetProps, assetIconForType, LabelButtonContext, LabelCollapseContextType, useExpand, LabelsPopoverContext, assetTableauIconForType } from '../LabelHelpers';
import { LabelDetailAssetStyle, LabelDetailAuthorStyle, LabelDetailDateStyle, LabelDetailMessageStyle, LabelDetailPermissionRequired, LabelExpandoContentDiv, LabelExpandoMaxWidth, SensitiveLabelColor } from '../Styles';
import { LabelCategories } from './LabelCategories';
import { LabelType, localizeLabelTypeName, stringtoLabelType } from './LabelType';

export type ExpandoButtonStyleElements = {
  readonly expandoType: 'default' | 'violet' | 'green' | 'blue' | 'yellow';
  readonly icon: JSX.Element;
}

const TextWithIconIcon = divWithStyle('TextWithIconIcon', {
  display: 'flex', // Necessary for the div to size correctly to its child icon
  flexShrink: 0,
  marginRight: TabStyles.Spacing.SmallSpace
});

const TextWithIconContainer = divWithStyle('TextWithIconContainer', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
});

function expandoStyle(category: string, isSevere?: boolean): ExpandoButtonStyleElements | null {
  switch (category) {
    case 'warning': return {
      expandoType: isSevere ? 'yellow' : 'blue',
      icon: isSevere ? <RESERVEDDatasourceCanWarningIcon size={14} alt={Messages.DataQualityTypeWarning()} color={'#AC4B02'} /> : <RESERVEDDatasourceCanInfoIcon size={14} alt={Messages.DataQualityTypeWarning()} color={'#05628A'} />
    };
    case 'certification': return {
      expandoType: 'green',
      icon: <CertifiedBaseIcon size={14} alt={Messages.ColumnHeaderCertification()} />
    };
    case 'sensitivity': return {
      expandoType: 'violet',
      icon: <KeyVerticalOutlineIcon size={14} style={{ color: SensitiveLabelColor }} />
    }
    default:
      assertUnreachable(category);
      return null;
  }
}

export function ExpandoWidgetWrapper(props: { labelType?: string, isSevere?: boolean, length: number, overrides?: LabelCollapseContextType, children: React.ReactNode }): JSX.Element {
  const [isExpanded, setIsExpanded] = useExpand(props.overrides);
  const { category } = React.useContext(LabelsPopoverContext);
  const buttonStyleElements = expandoStyle(category, props.isSevere);
  const labelType = props.labelType ?? '';

  return (
    <ExpandoWidget
      expandoType={buttonStyleElements?.expandoType ?? 'default'}
      direction={ExpandoDirection.Down}
      isExpanded={isExpanded}
      onCollapse={() => setIsExpanded(false)}
      onExpand={() => setIsExpanded(true)}
      labelIcon={buttonStyleElements?.icon}
      label={Messages.Label_type_chip_message({
        MESSAGE_COUNT: props.length,
        LABEL_TYPE_LOC: labelType in LabelType ? localizeLabelTypeName(stringtoLabelType(labelType)) : labelType,
        MESSAGE_COUNT_LOC: Formatters.formatCount(props.length)
      })}
      testId='expando-'
      alignment={'right'}
      style={{  
        marginBottom: '3px',
        maxWidth: LabelExpandoMaxWidth
      }}>

      <LabelExpandoContentDiv>{props.children}</LabelExpandoContentDiv>
    </ExpandoWidget>
  )
}


export type LabelDetailAuthorElementProps = {
  readonly severe?: boolean;
  readonly author: TableauUser;
} & LabelCategories;

export function LabelDetailAuthorElement(props: LabelDetailAuthorElementProps): JSX.Element {
  return (
    <LabelDetailAuthorStyle>
      <LabelDetailAuthorElementInner {...props} />
    </LabelDetailAuthorStyle>
  )
}

function LabelDetailAuthorLink(props: UserLinkProps) {
  return <a href={props.url} target='_blank' className={linkClass} title={props.name} rel="noreferrer">{props.name}</a>;
}

export function LabelDetailAuthorElementInner(props: LabelDetailAuthorElementProps): JSX.Element {
  const { linksDisabled } = React.useContext(LabelButtonContext);
  const userElement = (linksDisabled || !props.author.domain)
    ? <UserTextDisplayComponent user={props.author} />
    : <UserLinkDisplayComponent user={props.author} linkComponent={LabelDetailAuthorLink} />;
  const name = '[AUTHOR_NAME_TOKEN]';
  const span = <span key='author' data-tb-test-id={'Data-Quality-Detail-Author'}>{userElement}</span>;

  switch (props.category) {
    case 'warning':
      return (
        <>
          {interpolateString(Messages.DataQualityAuthor({ name }), { 'AUTHOR_NAME_TOKEN': span })}
        </>
      );
    case 'certification':
      return (
        <>
          {interpolateString(Messages.DataQualityAuthor({ name }), { 'AUTHOR_NAME_TOKEN': span })}
        </>
      );
    case 'sensitivity':
      return (
        <>
          {interpolateString(Messages.DataQualityAuthor({ name }), { 'AUTHOR_NAME_TOKEN': span })}
        </>
      );
    default:
      assertUnreachable(props);
      return <></>;
  }
}

export function TextLinkWidgetLabelDetailAsset(props: Omit<RenderLabelAssetProps, 'category' | 'labels'>): JSX.Element | null {
  const { content: baseContent } = React.useContext(LabelsPopoverContext);
  if (props.assetName == null) { return getPermissionRequiredMessage(); }
  const iconComponent = assetTableauIconForType(props.subjectArea ?? props.assetType, props.remoteType);
  return (
    <LabelDetailAssetStyle>
      {
        props.assetUrl ?
          <TextLinkWidget
            href={props.assetUrl}
            kind={TextLinkKind.Standalone}
            target='_blank'
            testId='text-link-widget-with-icon'
          >
            <TextWithIconContainer>
              <TextWithIconIcon>{iconComponent}</TextWithIconIcon>{props.assetName ?? baseContent?.contentName ?? null}
            </TextWithIconContainer>
          </TextLinkWidget>
          :
          <SingleLineTextWithIcon
            text={props.assetName ?? baseContent?.contentName ?? null}
            iconComponent={iconComponent}
            iconSize={'small'}
          />
      }
    </LabelDetailAssetStyle>
  )
}

export function LabelDetailAsset(props: Omit<RenderLabelAssetProps, 'category' | 'labels'>): JSX.Element | null {
  if (props.assetName == null) { return getPermissionRequiredMessage(); }
  const iconComponent = assetIconForType(props.subjectArea ?? props.assetType, props.remoteType);
  if (props.parentName) {
    const renderText = props.assetName + ' (table ' + props.parentName + ')';
    return (
      <LabelDetailAssetStyle>
        {
          props.assetUrl ?
            <SingleLineLinkWithSubLinkIcon
              linkText={props.assetName ?? null}
              linkHref={props.assetUrl ?? ''}
              iconComponent={iconComponent}
              iconSize={'small'}
              subLinkText={props.parentName ?? null}
              subLinkHref={props.parentUrl ?? ''}
              subPrefixText={' (table '}
              subSuffixText={')'}
            /> :
            <SingleLineTextWithIcon
              text={renderText}
              iconComponent={iconComponent}
              iconSize={'small'}
            />
        }
      </LabelDetailAssetStyle>
    );
  }
  return (
    <LabelDetailAssetStyle>
      {
        props.assetUrl ?
          <SingleLineLinkWithIcon
            linkText={props.assetName ?? null}
            linkHref={props.assetUrl ?? ''}
            iconComponent={iconComponent}
            iconSize={'small'}
          /> :
          <SingleLineTextWithIcon
            text={props.assetName || null}
            iconComponent={iconComponent}
            iconSize={'small'}
          />
      }
    </LabelDetailAssetStyle>
  )
}

export function LabelDetailDateElement(props: {
  readonly updatedAt: string;
}): JSX.Element {
  return (
    <LabelDetailDateStyle>
      {props.updatedAt}
    </LabelDetailDateStyle>
  )
}

export const LabelDetailMessageId = 'data-quality-detail-message-id';

export function LabelDetailMessageElement(props: {
  readonly assetName: string | null;
  readonly message: string | null;
}): JSX.Element {
  if (props.assetName === null) {
    return (
      <LabelDetailMessageStyle id={LabelDetailMessageId}>
        {Messages.PermissionRequiredMessage()}
      </LabelDetailMessageStyle>
    );
  }

  return (
    <LabelDetailMessageStyle>
      <MarkupOrSplitString {...props} />
    </LabelDetailMessageStyle>
  );
}

function MarkupOrSplitString(props: {
  readonly message: string | null;
}): JSX.Element {
  const { linksDisabled } = React.useContext(LabelButtonContext);
  const messageOrBlank = props.message || '';

  return (
    <FormattedMarkupWrapperInTooltip markup={messageOrBlank} linksDisabled={linksDisabled} />
  );
}

export function getPermissionRequiredMessage(): JSX.Element {
  const name = Messages.PermissionRequired();
  return <span key={'asset-' + name} style={LabelDetailPermissionRequired}>{name}</span>;
}
